import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Collapse,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { Spinner } from '../components';
import paymentServices from '../services/paymentServices';
import { AuthContext } from '../context/AuthProvider';

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const BUCKETS = ['0-30', '31-60', '61-90', '90+'];

const REQUEST_STATUS_COLORS = {
  sent: 'info',
  paid: 'success',
  partially_paid: 'warning',
  canceled: 'default',
  replaced: 'default',
  cancellation_pending: 'warning',
  delivery_failed: 'error',
  error: 'error',
};

const SummaryCard = ({ title, value, subtitle, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600, color: color || 'inherit' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Receivables = () => {
  const { userData } = useContext(AuthContext);
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [bucket, setBucket] = useState(null);
  const [noRequest, setNoRequest] = useState(false);
  const [includeLegacy, setIncludeLegacy] = useState(false);
  const [since, setSince] = useState('2026-02-01'); // CRM went live Feb 2026; older jobs have no payment records
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [issues, setIssues] = useState(null);
  const [showIssues, setShowIssues] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const isAdmin = ['superadmin', 'admin'].includes(userData?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentServices.getReceivables({
        query: query || undefined,
        bucket: bucket || undefined,
        noRequest: noRequest || undefined,
        includeLegacy: includeLegacy || undefined,
        since: includeLegacy ? undefined : since,
        skip: page * rowsPerPage,
        take: rowsPerPage,
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load receivables');
    } finally {
      setLoading(false);
    }
  }, [query, bucket, noRequest, includeLegacy, since, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  // debounce the search input
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(queryInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [queryInput]);

  const loadIssues = async () => {
    try {
      const res = await paymentServices.getReceivablesIssues();
      setIssues(res.data);
      setShowIssues(true);
    } catch (e) {
      toast.error('Failed to load sync issues');
    }
  };

  const runReconciliation = async () => {
    setReconciling(true);
    try {
      const res = await paymentServices.runReconciliation();
      const r = res.data;
      toast.success(
        `Reconciliation done: ${r.squarePaymentsSeen} Square payments checked, ` +
          `${r.paymentsRecorded} recovered, ${r.paymentsSettled} settled, ` +
          `${r.requestsRefreshed} invoices refreshed` +
          (r.errors?.length ? ` — ${r.errors.length} errors (see logs)` : '')
      );
      load();
    } catch (e) {
      toast.error('Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  if (!data && loading) return <Spinner />;
  if (!data) return null;

  const { summary, alerts, rows, total } = data;
  const alertCount =
    (alerts?.cancellationPending || 0) + (alerts?.deliveryFailed || 0) + (alerts?.syncIssues || 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Receivables
        </Typography>
        {isAdmin && (
          <Button
            variant="outlined"
            size="small"
            startIcon={reconciling ? <CircularProgress size={16} /> : <SyncIcon />}
            disabled={reconciling}
            onClick={runReconciliation}
          >
            {reconciling ? 'Reconciling…' : 'Reconcile with Square'}
          </Button>
        )}
      </Box>

      {alertCount > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => (showIssues ? setShowIssues(false) : loadIssues())}
        >
          Square sync needs attention: {alerts.cancellationPending} stuck cancellation(s),{' '}
          {alerts.deliveryFailed} delivery failure(s), {alerts.syncIssues} unmatched/error event(s).
          Click to {showIssues ? 'hide' : 'view'} details.
        </Alert>
      )}

      <Collapse in={showIssues && !!issues}>
        {issues && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Problem payment requests
            </Typography>
            {issues.requests.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            )}
            {issues.requests.map((r) => (
              <Typography key={r.id} variant="body2" sx={{ mb: 0.5 }}>
                <Chip size="small" label={r.status} color={REQUEST_STATUS_COLORS[r.status] || 'default'} sx={{ mr: 1 }} />
                Job {r.publicId} — {fmtMoney(r.amount)} — {r.statusReason || 'no details'}
              </Typography>
            ))}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Unmatched / failed Square events (30 days)
            </Typography>
            {issues.events.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            )}
            {issues.events.map((e) => (
              <Typography key={e.id} variant="body2" sx={{ mb: 0.5 }}>
                <Chip size="small" label={e.status} color="error" sx={{ mr: 1 }} />
                {e.eventType} — {e.error || e.eventId}
              </Typography>
            ))}
          </Paper>
        )}
      </Collapse>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Outstanding"
            value={fmtMoney(summary.outstandingTotal)}
            subtitle={`${summary.jobsCount} job(s)`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard title="Overdue (31+ days)" value={fmtMoney(summary.overdueTotal)} color="#d32f2f" />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard title="Pending ACH" value={fmtMoney(summary.pendingAchTotal)} color="#ed6c02" />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="No payment request"
            value={summary.noRequestCount}
            subtitle="jobs never invoiced"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {BUCKETS.map((b) => (
          <Chip
            key={b}
            label={`${b} days: ${fmtMoney(summary.aging[b]?.sum)} (${summary.aging[b]?.count || 0})`}
            color={bucket === b ? 'primary' : 'default'}
            variant={bucket === b ? 'filled' : 'outlined'}
            onClick={() => {
              setBucket(bucket === b ? null : b);
              setPage(0);
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Job #, client or address…"
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Jobs since"
          value={since}
          disabled={includeLegacy}
          onChange={(e) => {
            setSince(e.target.value);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={noRequest}
              onChange={(e) => {
                setNoRequest(e.target.checked);
                setPage(0);
              }}
            />
          }
          label="Never invoiced only"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={includeLegacy}
              onChange={(e) => {
                setIncludeLegacy(e.target.checked);
                setPage(0);
              }}
            />
          }
          label="Include legacy (all years)"
        />
        {loading && <CircularProgress size={20} />}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Job #</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Address</TableCell>
              <TableCell align="right">Contract</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Pending</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell align="right">Age</TableCell>
              <TableCell>Last request</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.publicId}</TableCell>
                <TableCell>{r.clientName || '—'}</TableCell>
                <TableCell>{r.address || '—'}</TableCell>
                <TableCell align="right">{fmtMoney(r.contractPrice)}</TableCell>
                <TableCell align="right">{fmtMoney(r.paid)}</TableCell>
                <TableCell align="right">{r.pending > 0 ? fmtMoney(r.pending) : '—'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {fmtMoney(r.balance)}
                </TableCell>
                <TableCell align="right" sx={{ color: r.ageDays > 30 ? '#d32f2f' : 'inherit' }}>
                  {r.ageDays}d
                </TableCell>
                <TableCell>
                  {r.lastRequestStatus ? (
                    <Tooltip
                      title={
                        r.lastRequestAt
                          ? `${fmtMoney(r.lastRequestAmount)} on ${new Date(r.lastRequestAt).toLocaleDateString()}`
                          : ''
                      }
                    >
                      <Chip
                        size="small"
                        label={r.lastRequestStatus}
                        color={REQUEST_STATUS_COLORS[r.lastRequestStatus] || 'default'}
                      />
                    </Tooltip>
                  ) : (
                    <Chip size="small" label="never" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Open job (send invoice from there)">
                    <IconButton size="small" onClick={() => navigate(`/proposals/view/${r.id}`)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No outstanding balances match the filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </TableContainer>
    </Box>
  );
};

export default Receivables;
